import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Shield, BarChart3, Hash, CheckCircle2, AlertTriangle,
  Loader2, FileText, Key
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import api, { getPublicAudit } from "../../lib/api";
import { toast } from "sonner";
import { formatDateTimeTz } from "../../lib/dateUtils";

export function AuditTab({ consulta, id, navigate }) {
  const { t } = useTranslation();
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [handVoteRequests, setHandVoteRequests] = useState([]);
  const [handVoteLoading, setHandVoteLoading] = useState(false);

  useEffect(() => {
    loadAuditData();
    if (consulta?.enable_vot_ma_alcada) {
      api.get(`/hand-vote/status/${id}`).then(res => setHandVoteRequests(res.data)).catch(() => {});
    }
  }, [id, consulta]);

  const loadAuditData = async () => {
    if (!consulta?.enable_vot_ma_alcada) return;
    setAuditLoading(true);
    try {
      const res = await getPublicAudit(id);
      setAuditData(res.data);
    } catch {
      setAuditData(null);
    } finally {
      setAuditLoading(false);
    }
  };

  const handleRequestHandVote = async () => {
    setHandVoteLoading(true);
    try {
      await api.post("/hand-vote/request", { consulta_id: id });
      toast.success(t('results.requestSent'));
      const statusRes = await api.get(`/hand-vote/status/${id}`);
      setHandVoteRequests(statusRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('results.requestError'));
    } finally {
      setHandVoteLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="audit-tab">
      {/* Public Audit Section */}
      {auditLoading ? (
        <div className="flex items-center justify-center py-12"><div className="spinner" /></div>
      ) : auditData ? (
        <>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`rounded-lg p-6 border shadow-sm ${auditData.integrity_verified ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 rounded-lg ${auditData.integrity_verified ? 'bg-emerald-100' : 'bg-red-100'}`}>
                  {auditData.integrity_verified ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <AlertTriangle className="h-5 w-5 text-red-600" />}
                </div>
                <span className="text-sm text-slate-500">{t('results.integrity')}</span>
              </div>
              <p className={`text-xl font-bold ${auditData.integrity_verified ? 'text-emerald-700' : 'text-red-700'}`}>
                {auditData.integrity_verified ? t('results.verified') : t('results.issuesDetected')}
              </p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-100 rounded-lg"><BarChart3 className="h-5 w-5 text-blue-600" /></div>
                <span className="text-sm text-slate-500">{t('results.totalAuditedVotes')}</span>
              </div>
              <p className="text-3xl font-bold text-slate-900">{auditData.total_votes}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-purple-100 rounded-lg"><Hash className="h-5 w-5 text-purple-600" /></div>
                <span className="text-sm text-slate-500">{t('results.globalHash')}</span>
              </div>
              <p className="text-xs font-mono text-slate-700 break-all leading-relaxed mt-1">{auditData.audit_hash?.substring(0, 32)}...</p>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-heading font-semibold text-slate-900 mb-3">{t('results.globalAuditHash')}</h3>
            <p className="text-sm text-slate-500 mb-3">{t('results.globalAuditHashDesc')}</p>
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <code className="text-sm font-mono text-slate-800 break-all select-all">{auditData.audit_hash}</code>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-800 mb-1">{t('results.howHandVoteWorks')}</p>
                <p className="text-xs text-blue-700 leading-relaxed">
                  {t('results.handVoteExplanation')}
                </p>
              </div>
            </div>
          </motion.div>
        </>
      ) : (
        <div className="text-center py-12">
          <Shield className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">{t('results.couldNotLoadHandVote')}</p>
        </div>
      )}

      {/* Hand Vote XLSX Request Section */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        className="bg-white border border-amber-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-amber-100 rounded-lg"><FileText className="h-5 w-5 text-amber-600" /></div>
          <div>
            <h3 className="text-lg font-heading font-semibold text-slate-900">{t('results.xlsxHandVote')}</h3>
            <p className="text-sm text-slate-500">
              {t('results.xlsxHandVoteDesc')}
            </p>
          </div>
        </div>

        {handVoteLoading ? (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /><span className="text-sm">{t('results.loadingState')}</span>
          </div>
        ) : (
          <div className="space-y-3">
            {!handVoteRequests.some(r => r.status === 'pending' || r.status === 'admin_approved') ? (
              <Button onClick={handleRequestHandVote} className="bg-amber-600 hover:bg-amber-700" data-testid="hand-vote-request-btn">
                <FileText className="h-4 w-4 mr-2" />{t('results.requestXlsx')}
              </Button>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700" data-testid="hand-vote-pending-msg">
                {handVoteRequests.some(r => r.status === 'pending')
                  ? t('results.pendingSentMsg')
                  : t('results.adminApprovedMsg')}
              </div>
            )}
            {handVoteRequests.length > 0 && (
              <div className="mt-4 border-t border-slate-200 pt-4">
                <p className="text-sm font-medium text-slate-700 mb-2">{t('results.requestHistory')}</p>
                <div className="space-y-2">
                  {handVoteRequests.map((req, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-slate-50 p-3 rounded-lg text-sm">
                      <div>
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                          req.status === 'completed' ? 'bg-green-100 text-green-700' :
                          req.status === 'admin_approved' ? 'bg-blue-100 text-blue-700' :
                          req.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {req.status === 'completed' ? t('results.statusCompleted') :
                           req.status === 'admin_approved' ? t('results.statusApproved') :
                           req.status === 'pending' ? t('results.statusPendingAdmin') :
                           req.status === 'expired' ? t('results.statusExpired') : req.status}
                        </span>
                        {req.created_at && <span className="ml-2 text-xs text-slate-400">{formatDateTimeTz(req.created_at, consulta?.entity_timezone)}</span>}
                      </div>
                      {req.status === 'admin_approved' && req.token && (
                        <Button size="sm" className="bg-amber-600 hover:bg-amber-700"
                          onClick={() => navigate(`/hand-vote/complete/${req.token}`)} data-testid="hand-vote-complete-btn">
                          <Key className="h-3 w-3 mr-1" />{t('results.completeBtn')}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
}
